import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Warning, Sparkle, ImageSquare } from "@phosphor-icons/react";

const riskClass = (r) => r === "alto" ? "risk-alto" : r === "medio" ? "risk-medio" : "risk-baixo";

export default function ResultDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [brand, setBrand] = useState(null);
  const [note, setNote] = useState("");
  const [visualLoading, setVisualLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/results/${id}`);
    setR(data);
    setNote(data.observacao_ia || "");
    if (data.brand_id) {
      try {
        const { data: b } = await api.get(`/brands/${data.brand_id}`);
        setBrand(b);
      } catch {}
    }
  };
  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    await api.patch(`/results/${id}`, { status });
    toast.success("Status atualizado");
    load();
  };

  const saveNote = async () => {
    await api.patch(`/results/${id}`, { observacao: note });
    toast.success("Observação salva");
  };

  const analyze = async () => {
    toast.info("Analisando com IA...");
    try {
      const { data } = await api.post(`/results/${id}/analyze`);
      setNote(data.analysis);
      toast.success("Análise concluída");
    } catch (e) { toast.error("Falha na análise"); }
  };

  const visualAnalyze = async () => {
    setVisualLoading(true);
    toast.info("Analisando visualmente (CLIP + pHash + cor)...");
    try {
      await api.post(`/visual/compare`, { brand_id: r.brand_id, result_ids: [r.id] });
      toast.success("Análise visual concluída");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha na análise visual"); }
    finally { setVisualLoading(false); }
  };

  if (!r) return <div className="text-sm text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="result-detail-page">
      <Link to="/app/resultados" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-[#0A3B2C]"><ArrowLeft size={14} /> Voltar</Link>

      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Detalhes do resultado</div>
          <h1 className="font-heading font-extrabold text-3xl">{r.nome_encontrado}</h1>
          <p className="text-sm text-muted-foreground mt-1">{r.segmento} • {r.cidade}{r.estado && `/${r.estado}`}</p>
        </div>
        <span className={`px-3 py-1 text-xs font-bold rounded-sm ${riskClass(r.grau_risco)}`}>RISCO {r.grau_risco.toUpperCase()}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 rounded-sm border lg:col-span-2">
          <h3 className="font-heading font-bold mb-4">Informações públicas</h3>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-[10px] uppercase text-muted-foreground">Fonte</dt><dd>{r.fonte}</dd></div>
            <div><dt className="text-[10px] uppercase text-muted-foreground">Termo pesquisado</dt><dd>{r.termo_pesquisado}</dd></div>
            <div><dt className="text-[10px] uppercase text-muted-foreground">Similaridade nome</dt><dd className="font-mono">{r.similaridade_nome}%</dd></div>
            <div><dt className="text-[10px] uppercase text-muted-foreground">Similaridade visual</dt><dd className="font-mono">{r.similaridade_visual}%</dd></div>
            <div className="col-span-2"><dt className="text-[10px] uppercase text-muted-foreground">URL</dt><dd><a href={r.url} target="_blank" rel="noreferrer" className="text-[#0A3B2C] underline break-all">{r.url}</a></dd></div>
            <div><dt className="text-[10px] uppercase text-muted-foreground">Descoberto em</dt><dd className="font-mono text-xs">{new Date(r.data_descoberta).toLocaleString("pt-BR")}</dd></div>
            <div><dt className="text-[10px] uppercase text-muted-foreground">Status</dt><dd>{r.status}</dd></div>
          </dl>

          {r.inpi_data && (
            <div className="mt-6 p-4 bg-[#F3F4F6] rounded-sm border-l-4 border-[#0A3B2C]">
              <div className="font-heading font-bold text-sm mb-2">Dados do INPI</div>
              <dl className="grid grid-cols-2 gap-2 text-xs">
                <div><dt className="text-muted-foreground">Nº Processo</dt><dd className="font-mono">{r.inpi_data.numero_processo}</dd></div>
                <div><dt className="text-muted-foreground">Classe</dt><dd>{r.inpi_data.classe}</dd></div>
                <div><dt className="text-muted-foreground">Situação</dt><dd>{r.inpi_data.situacao}</dd></div>
                <div><dt className="text-muted-foreground">Apresentação</dt><dd>{r.inpi_data.apresentacao}</dd></div>
                <div><dt className="text-muted-foreground">Data depósito</dt><dd className="font-mono">{r.inpi_data.data_deposito}</dd></div>
                <div><dt className="text-muted-foreground">Titular</dt><dd>{r.inpi_data.titular}</dd></div>
              </dl>
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-sm border">
          <h3 className="font-heading font-bold mb-4">Ações</h3>
          <div className="space-y-2">
            <Button data-testid="action-confirm" className="w-full rounded-sm bg-[#DC2626] hover:bg-red-700" onClick={() => updateStatus("conflito_confirmado")}><Warning size={14} className="mr-2" /> Confirmar conflito</Button>
            <Button data-testid="action-review" variant="outline" className="w-full rounded-sm" onClick={() => updateStatus("revisado")}><Check size={14} className="mr-2" /> Marcar revisado</Button>
            <Button data-testid="action-discard" variant="outline" className="w-full rounded-sm" onClick={() => updateStatus("descartado")}><X size={14} className="mr-2" /> Descartar</Button>
            <Button data-testid="action-analyze" variant="outline" className="w-full rounded-sm border-[#0A3B2C] text-[#0A3B2C]" onClick={analyze}><Sparkle size={14} className="mr-2" /> Analisar com IA</Button>
            {r.fonte === "SerpApi (Google Images)" && (
              <Button data-testid="action-visual-analyze" variant="outline" disabled={visualLoading} className="w-full rounded-sm border-[#0A3B2C] text-[#0A3B2C]" onClick={visualAnalyze}>
                <ImageSquare size={14} className="mr-2" /> {visualLoading ? "Processando..." : "Analisar visualmente"}
              </Button>
            )}
          </div>
          <div className="mt-4 text-[10px] text-muted-foreground border-t pt-3">
            Ação recomendada:<br/>
            <span className="text-foreground font-semibold">{r.acao_recomendada}</span>
          </div>
        </Card>
      </div>

      {r.visual_analysis && (
        <Card className="p-5 rounded-sm border" data-testid="visual-analysis-card">
          <div className="flex items-center gap-2 mb-4">
            <ImageSquare size={20} weight="bold" className="text-[#0A3B2C]" />
            <h3 className="font-heading font-bold">Análise visual local — {r.visual_analysis.engine}</h3>
          </div>

          {/* Side-by-side images */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-center mb-6">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Logo oficial (referência)</div>
              <div className="aspect-square bg-white border rounded-sm flex items-center justify-center p-4">
                {brand?.logo_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt="ref" className="max-w-full max-h-full object-contain" data-testid="ref-image" />
                ) : <span className="text-xs text-muted-foreground">Logo não disponível</span>}
              </div>
              <div className="mt-2 text-xs font-semibold">{brand?.nome_marca}</div>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Similaridade final</div>
              <div className={`w-24 h-24 rounded-full border-4 flex items-center justify-center font-heading font-extrabold text-2xl ${r.visual_analysis.metrics.final_pct >= 70 ? "border-[#DC2626] text-[#DC2626]" : r.visual_analysis.metrics.final_pct >= 40 ? "border-[#B45309] text-[#B45309]" : "border-[#047857] text-[#047857]"}`} data-testid="final-pct">
                {r.visual_analysis.metrics.final_pct}%
              </div>
              <div className="text-[10px] uppercase font-bold" data-testid="visual-status">{r.visual_analysis.metrics.status}</div>
            </div>

            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Logo candidato</div>
              <div className="aspect-square bg-white border rounded-sm flex items-center justify-center p-4">
                {r.imagem_url ? (
                  <img src={r.imagem_url} alt="candidato" className="max-w-full max-h-full object-contain" data-testid="candidate-image" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : <span className="text-xs text-muted-foreground">Imagem indisponível</span>}
              </div>
              <div className="mt-2 text-xs font-semibold truncate">{r.nome_encontrado}</div>
            </div>
          </div>

          {/* Per-metric breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <MetricBox label="CLIP (embedding ViT-B/32)" pct={r.visual_analysis.metrics.clip.pct} detail={`cos=${r.visual_analysis.metrics.clip.cosine} · ${r.visual_analysis.metrics.clip.ms}ms`} testid="metric-clip" />
            <MetricBox label="pHash (perceptual)" pct={r.visual_analysis.metrics.phash.pct} detail={`hamming=${r.visual_analysis.metrics.phash.hamming}/${r.visual_analysis.metrics.phash.bits} · ${r.visual_analysis.metrics.phash.ms}ms`} testid="metric-phash" />
            <MetricBox label="Similaridade de cores (histograma RGB)" pct={r.visual_analysis.metrics.color.pct} detail={`cos=${r.visual_analysis.metrics.color.cosine} · ${r.visual_analysis.metrics.color.ms}ms`} testid="metric-color" />
          </div>

          {/* Formula + origin */}
          <div className="p-3 bg-[#F3F4F6] border-l-4 border-[#0A3B2C] text-xs font-mono" data-testid="visual-formula">
            <strong>Fórmula:</strong> {r.visual_analysis.metrics.formula}
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            Página de origem: <a href={r.url} target="_blank" rel="noreferrer" className="text-[#0A3B2C] underline" data-testid="page-origin">{r.url}</a>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            Análise realizada em {new Date(r.visual_analysis.analyzed_at).toLocaleString("pt-BR")} · Motor local, sem custo externo.
          </div>
        </Card>
      )}

      <Card className="p-5 rounded-sm border">
        <h3 className="font-heading font-bold mb-3">Observação / Análise IA</h3>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="rounded-sm" data-testid="result-note" />
        <div className="flex justify-end mt-2">
          <Button onClick={saveNote} className="rounded-sm bg-[#0A3B2C] hover:bg-[#083527]" data-testid="save-note-btn">Salvar observação</Button>
        </div>
        <div className="mt-3 text-[10px] text-muted-foreground border-t pt-2">
          Esta classificação é informativa e não substitui análise jurídica especializada.
        </div>
      </Card>
    </div>
  );
}

const MetricBox = ({ label, pct, detail, testid }) => (
  <div className="p-3 border rounded-sm" data-testid={testid}>
    <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{label}</div>
    <div className="font-heading font-bold text-2xl mt-1">{pct}%</div>
    <div className="text-[10px] text-muted-foreground font-mono mt-1">{detail}</div>
    <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
      <div className="h-full bg-[#0A3B2C]" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  </div>
);
